import axios from "axios";
import { message } from "antd";

const DEV_API_FALLBACK = "http://127.0.0.1:5000/mailinflow";

/** Resolve API base URL (absolute) for the current environment. */
export function getBaseUrl() {
  const configured = (process.env.REACT_APP_BASE_URL || "").trim();
  if (/^https?:\/\//i.test(configured)) {
    return configured.replace(/\/$/, "");
  }
  if (configured.startsWith("/")) {
    if (typeof window !== "undefined") {
      return `${window.location.origin}${configured}`.replace(/\/$/, "");
    }
    return configured.replace(/\/$/, "");
  }
  if (typeof window !== "undefined") {
    return DEV_API_FALLBACK;
  }
  return configured || DEV_API_FALLBACK;
}

/** @deprecated Prefer getBaseUrl() — kept for modules that build URLs manually. */
export const baseUrl = getBaseUrl();

export function buildApiUrl(path, query) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  let url = `${getBaseUrl()}${normalizedPath}`;
  if (query && Object.keys(query).length > 0) {
    const qs = new URLSearchParams(query).toString();
    if (qs) url += `?${qs}`;
  }
  return url;
}

const FALLBACK_ERROR_MSG = "Something went wrong. Please try again.";

/** Public GET without global error toast (hosted form, etc.). */
export function publicFetchJson(path, query) {
  return axios.get(buildApiUrl(path, query), {
    headers: { Accept: "application/json" },
  });
}

/** Public POST without global error toast. */
export function publicPostJson(path, body) {
  return axios.post(buildApiUrl(path), body, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
  });
}

/** Public multipart POST (form file fields) — omit Content-Type so axios/the
 * browser sets it with the correct multipart boundary. */
export function publicPostMultipart(path, formData) {
  return axios.post(buildApiUrl(path), formData, {
    headers: { Accept: "application/json" },
  });
}

export const publicGetApi = (path, payload) => {
  return new Promise((resolve, reject) => {
    axios
      .get(buildApiUrl(path, payload))
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const publicPostApi = (path, payload) => {
  return new Promise((resolve, reject) => {
    axios
      .post(buildApiUrl(path), { ...payload })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

const rejectIfSessionExpired = () => {
  if (
    !localStorage.getItem("accessToken") ||
    !(JSON.parse(localStorage.getItem("expireAt")) > new Date().getTime())
  ) {
    message.error("Session expired, please login again", 6);
    return Promise.reject(new Error("Session expired"));
  }
  return null;
};

/** Authenticated multipart POST (e.g. campaign attachment upload) — omit
 * Content-Type so axios/the browser sets it with the correct boundary;
 * `payload` must already be a FormData instance, not spread like the other
 * private*Api helpers do, since spreading a FormData yields {}. */
export const privatePostMultipart = (path, formData) => {
  const sessionError = rejectIfSessionExpired();
  if (sessionError) return sessionError;
  const accessToken = localStorage.getItem("accessToken");
  return new Promise((resolve, reject) => {
    axios
      .post(buildApiUrl(path), formData, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => resolve(response))
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg = raw || FALLBACK_ERROR_MSG;
        message.error(msg);
        reject(error);
      });
  });
};

export const privatePostApi = (path, payload) => {
  const sessionError = rejectIfSessionExpired();
  if (sessionError) return sessionError;
  const accessToken = localStorage.getItem("accessToken");
  return new Promise((resolve, reject) => {
    axios
      .post(
        buildApiUrl(path),
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privateGetApi = (path, payload) => {
  const sessionError = rejectIfSessionExpired();
  if (sessionError) return sessionError;
  const accessToken = localStorage.getItem("accessToken");
  return new Promise((resolve, reject) => {
    axios
      .get(buildApiUrl(path, payload), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privatePutApi = (path, payload) => {
  const sessionError = rejectIfSessionExpired();
  if (sessionError) return sessionError;
  const accessToken = localStorage.getItem("accessToken");
  return new Promise((resolve, reject) => {
    axios
      .put(
        buildApiUrl(path),
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privateDeleteApi = (path, payload) => {
  const sessionError = rejectIfSessionExpired();
  if (sessionError) return sessionError;
  const accessToken = localStorage.getItem("accessToken");
  return new Promise((resolve, reject) => {
    axios
      .delete(buildApiUrl(path), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privateClientPostApi = (path, payload) => {
  if (
    !localStorage.getItem("clientAccessToken") ||
    !(JSON.parse(localStorage.getItem("clientExpireAt")) > new Date().getTime())
  ) {
    message.error("Session expired, please login again", 6);
    return;
  }
  const accessToken = localStorage.getItem("clientAccessToken");
  return new Promise((resolve, reject) => {
    axios
      .post(
        buildApiUrl(path),
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privateClientPutApi = (path, payload) => {
  if (
    !localStorage.getItem("clientAccessToken") ||
    !(JSON.parse(localStorage.getItem("clientExpireAt")) > new Date().getTime())
  ) {
    message.error("Session expired, please login again", 6);
    return;
  }
  const accessToken = localStorage.getItem("clientAccessToken");
  return new Promise((resolve, reject) => {
    axios
      .put(
        buildApiUrl(path),
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

/** Admin portal (/admin) auth namespace -- separate from the regular
 * accessToken/user session, mirrors the privateClient* pattern above. */
export const privateAdminGetApi = (path, payload) => {
  if (
    !localStorage.getItem("adminAccessToken") ||
    !(JSON.parse(localStorage.getItem("adminExpireAt")) > new Date().getTime())
  ) {
    message.error("Session expired, please login again", 6);
    return;
  }
  const accessToken = localStorage.getItem("adminAccessToken");
  return new Promise((resolve, reject) => {
    axios
      .get(buildApiUrl(path, payload), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privateAdminPostApi = (path, payload) => {
  if (
    !localStorage.getItem("adminAccessToken") ||
    !(JSON.parse(localStorage.getItem("adminExpireAt")) > new Date().getTime())
  ) {
    message.error("Session expired, please login again", 6);
    return;
  }
  const accessToken = localStorage.getItem("adminAccessToken");
  return new Promise((resolve, reject) => {
    axios
      .post(
        buildApiUrl(path),
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privateAdminPutApi = (path, payload) => {
  if (
    !localStorage.getItem("adminAccessToken") ||
    !(JSON.parse(localStorage.getItem("adminExpireAt")) > new Date().getTime())
  ) {
    message.error("Session expired, please login again", 6);
    return;
  }
  const accessToken = localStorage.getItem("adminAccessToken");
  return new Promise((resolve, reject) => {
    axios
      .put(
        buildApiUrl(path),
        { ...payload },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};

export const privateAdminDeleteApi = (path) => {
  if (
    !localStorage.getItem("adminAccessToken") ||
    !(JSON.parse(localStorage.getItem("adminExpireAt")) > new Date().getTime())
  ) {
    message.error("Session expired, please login again", 6);
    return;
  }
  const accessToken = localStorage.getItem("adminAccessToken");
  return new Promise((resolve, reject) => {
    axios
      .delete(buildApiUrl(path), {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })
      .then((response) => {
        resolve(response);
      })
      .catch((error) => {
        const raw = error.response?.data?.message ?? error.message;
        const msg =
          raw && String(raw).trim() ? String(raw).trim() : FALLBACK_ERROR_MSG;
        message.error(msg, 6);
        reject(error);
      });
  });
};
